// src/components/AnalysisSections.tsx
import React, { memo, useEffect, useState } from "react";
import { parseAnalysisContent } from "../utils";

interface AnalysisSectionsProps {
  analysis: string;
}

const AnalysisSections: React.FC<AnalysisSectionsProps> = memo(({ analysis }) => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  
  const emojis = [
    // Original emojis (with and without variation selectors)
    "🫀", "🫁", "🧠", "💧", "🔬", "💊", "⚠️", "⚠", "🚨", "⏰", "🔍", "👨‍⚕️",
    "🏥", "😴", "📊", "🩹", "🤢", "🍽️", "🍽", "🚶‍♂️", "👶", "🍼", "💉", "👪",
    // New department emojis (with and without variation selectors)
    "🎗️", "🎗", "🧪", "🤒", "🩺", "❤️", "❤", "📈", "👀", "🤰", "🩸", "🗣️", "🗣", "🧩", "👥",
    "🦾", "🏋️", "🏋", "🚻", "📋", "🧸", "🍎", "👨‍👩‍👧"
  ];
  const sectionColors: { [key: string]: string } = {
    // Original colors (with and without variation selectors)
    "🫀": "#e74c3c", "🫁": "#3498db", "🧠": "#9b59b6", "💧": "#1abc9c",
    "🔬": "#f39c12", "💊": "#e67e22", "⚠️": "#f1c40f", "⚠": "#f1c40f", "🚨": "#e74c3c",
    "⏰": "#16a085", "🔍": "#2980b9", "👨‍⚕️": "#8e44ad", "🏥": "#27ae60",
    "😴": "#6c5ce7", "📊": "#fd79a8", "🩹": "#a29bfe", "🤢": "#fdcb6e",
    "🍽️": "#e17055", "🍽": "#e17055", "🚶‍♂️": "#00b894", "👶": "#ff7675", "🍼": "#74b9ff",
    "💉": "#fd79a8", "👪": "#55a3ff",
    // New department colors (with and without variation selectors)
    "🎗️": "#ff69b4", "🎗": "#ff69b4", "🧪": "#20c997", "🤒": "#fd7e14", "🩺": "#17a2b8",
    "❤️": "#dc3545", "❤": "#dc3545", "📈": "#28a745", "👀": "#6f42c1", "🤰": "#e83e8c",
    "🩸": "#c0392b", "🗣️": "#3498db", "🗣": "#3498db", "🧩": "#9b59b6", "👥": "#2ecc71",
    "🦾": "#e67e22", "🏋️": "#27ae60", "🏋": "#27ae60", "🚻": "#16a085", "📋": "#3498db",
    "🧸": "#f39c12", "🍎": "#e74c3c", "👨‍👩‍👧": "#9b59b6"
  };
  
  const result: Array<{ emoji: string, content: string }> = [];
  const re = new RegExp(`(${emojis.join("|")})`, "g");
  let match, lastIdx = 0, lastEmoji = "";
  
  while ((match = re.exec(analysis)) !== null) {
    if (lastEmoji && lastIdx < match.index) {
      result.push({ emoji: lastEmoji, content: analysis.slice(lastIdx, match.index) });
    }
    lastEmoji = match[1];
    lastIdx = match.index + match[1].length;
  }
  
  if (lastEmoji && lastIdx < analysis.length) {
    result.push({ emoji: lastEmoji, content: analysis.slice(lastIdx) });
  }

  // Staggered animation effect - show cards one by one
  useEffect(() => {
    setVisibleCards([]); // Reset when analysis changes
    
    if (result.length === 0) return;
    
    // Show each card with a staggered delay
    result.forEach((_, index) => {
      setTimeout(() => {
        setVisibleCards(prev => [...prev, index]);
      }, index * 150); // 150ms delay between each card
    });
  }, [analysis, result.length]);

  return (
    <div className="analysis-sections">
      {result.length === 0 ? (
        <div className="section-card slide-in-card" style={{ borderLeftColor: "#667eea" }}>
          <div 
            style={{ 
              whiteSpace: "pre-wrap"
            }}
            dangerouslySetInnerHTML={{ __html: parseAnalysisContent(analysis) }}
          />
        </div>
      ) : (
        result.map(({ emoji, content }, i) => {
          const lines = content.trim().split("\n");
          // Strip any HTML tags from the title (headers should never have span tags)
          const title = (lines[0] || "").replace(/<[^>]*>/g, '');
          const body = lines.slice(1).join("\n");
          const isVisible = visibleCards.includes(i);
          
          return (
            <div 
              className={`category-section ${isVisible ? 'slide-in-card visible' : 'slide-in-card'}`}
              key={i}
              style={{ borderLeftColor: sectionColors[emoji] || "#667eea" }}
            >
              <h3 style={{ color: sectionColors[emoji], fontSize: "1.15rem", fontWeight: 700 }}>
                {emoji} {title}
              </h3>
              <div 
                style={{ 
                  lineHeight: 1.6, 
                  whiteSpace: "pre-wrap"
                }}
                dangerouslySetInnerHTML={{ __html: parseAnalysisContent(body) }}
              />
            </div>
          );
        })
      )}
    </div>
  );
});

AnalysisSections.displayName = 'AnalysisSections';

export default AnalysisSections;